import Image from 'next/image';
import { formatDate } from '@core/utils/dateUtils';
import { formatReadingTime } from '@core/utils/timeUtils';
import { Box, Flex, Show, Stack, HStack, Heading } from '@react/chakra';

import type { JSX } from 'react';
import type { IPostVMProps } from '@data/view-models';

import { Link } from '~/components';
import { Topic } from '~/features/topics';

import { postTitle } from '../../../utils';

interface IPostProps {
  data: IPostVMProps;
}

export function Post({ data }: IPostProps): JSX.Element {
  const {
    slug,
    image,
    thumbnail,
    seriesPost,
    readingTime,
    publishedAt,
    topics = [],
  } = data;

  const { chapterSlug } = seriesPost || {};

  return (
    <Flex
      borderRadius='8px'
      bgColor='gray.700'
      transitionDuration='.15s'
      transitionTimingFunction='cubic-bezier(.4, 0, .2,1)'
      transitionProperty='color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter'
    >
      <Link
        href={`/posts/${chapterSlug || slug}`}
        containerProps={{
          w: '100%',
          h: '100%',
          color: 'unset',
          borderRadius: '8px',
          transitionDuration: '.3s',
          transitionTimingFunction: 'cubic-bezier(.4, 0, .2,1)',
          transitionProperty:
            'color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter',
          _hover: {
            boxShadow: 'eclipse',
            borderColor: 'transparent',
          },
        }}
      >
        <Stack h='100%' borderRadius='8px' gap={2}>
          <Flex minH='500px'>
            <Show when={!!(thumbnail || image)}>
              <Box asChild objectFit='cover' borderTopRadius='8px'>
                <Image
                  priority
                  width={333}
                  height={500}
                  alt='Post Image'
                  src={thumbnail || image || ''}
                />
              </Box>
            </Show>
          </Flex>
          <Stack
            px={6}
            gap={8}
            flex={1}
            py={{
              base: 6,
              md: 10,
            }}
          >
            <Heading size='2xl'>{postTitle(data)}</Heading>
            <Box flex={1}>
              <HStack flexWrap='wrap' alignItems='flex-start'>
                {topics.map((topic) => {
                  return (
                    <Topic
                      size='xs'
                      data={topic}
                      key={topic.slug}
                      variant='normal'
                    />
                  );
                })}
              </HStack>
            </Box>
            <Heading size='md' variant='sub' justifySelf='flex-end'>
              {formatDate(publishedAt)} —{' '}
              {formatReadingTime(readingTime, 'min')} read
            </Heading>
          </Stack>
        </Stack>
      </Link>
    </Flex>
  );
}
